import { SupportedChainId } from 'constants/chains'
import { transparentize } from 'polished'
import { ReactNode } from 'react'
import { AlertTriangle } from 'react-feather'
import styled, { css } from 'styled-components/macro'
import { Z_INDEX } from 'theme/zIndex'

import { AutoColumn } from '../Column'

export const PageWrapper = styled.div<{ $wide?: boolean }>`
  padding: 5px 0px 0px;
  max-width: ${({ $wide }) => ($wide ? '1200px' : '440px')};
  width: 100%;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    padding-top: 0px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding-top: 0px;
  }
`

// Mostly copied from `AppBody` but it was getting too hard to maintain backwards compatibility.
export const SwapWrapper = styled.main<{ chainId?: number }>`
  position: relative;
  background: ${({ theme }) => theme.backgroundSurface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.backgroundBackdrop};
  padding: 16px;
  padding-top: 12px;
  box-shadow: ${({ chainId }) => !!chainId && chainId === SupportedChainId.BNB && '0px 40px 120px 0px #f0b90b29'};
  z-index: ${Z_INDEX.default};
  transition: transform 250ms ease;

  &:hover {
    border: 0.5px solid ${({ theme }) => theme.backgroundBackdrop};
  }
  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.xs}px`}) {
    padding: 16px 12px;
  }
`

export const ArrowWrapper = styled.div<{ clickable: boolean }>`
  border-radius: 6px;
  height: 32px;
  width: 32px;
  position: relative;
  margin-top: -13px;
  margin-bottom: -13px;
  margin-left: auto;
  margin-right: auto;
  background-color: ${({ theme }) => theme.backgroundModule};
  border: 3px solid;
  border-color: ${({ theme }) => theme.backgroundSurface};

  z-index: 2;
  ${({ clickable }) =>
    clickable
      ? css`
          :hover {
            cursor: pointer;
            opacity: 0.8;
          }
        `
      : null}
`

// styles
export const Dots = styled.span`
  &::after {
    display: inline-block;
    animation: ellipsis 1.25s infinite;
    content: '.';
    width: 1em;
    text-align: left;
  }
  @keyframes ellipsis {
    0% {
      content: '.';
    }
    33% {
      content: '..';
    }
    66% {
      content: '...';
    }
  }
`

const SwapCallbackErrorInner = styled.div`
  background-color: ${({ theme }) => transparentize(0.9, theme.accentFailure)};
  border-radius: 1rem;
  display: flex;
  align-items: center;
  font-size: 0.825rem;
  width: 100%;
  padding: 3rem 1.25rem 1rem 1rem;
  margin-top: -2rem;
  color: ${({ theme }) => theme.accentFailure};
  z-index: -1;
  p {
    padding: 0;
    margin: 0;
    font-weight: 500;
  }
`

const SwapCallbackErrorInnerAlertTriangle = styled.div`
  background-color: ${({ theme }) => transparentize(0.9, theme.accentFailure)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  border-radius: 12px;
  min-width: 48px;
  height: 48px;
`

export function SwapCallbackError({ error }: { error: ReactNode }) {
  return (
    <SwapCallbackErrorInner>
      <SwapCallbackErrorInnerAlertTriangle>
        <AlertTriangle size={24} />
      </SwapCallbackErrorInnerAlertTriangle>
      <p style={{ wordBreak: 'break-word' }}>{error}</p>
    </SwapCallbackErrorInner>
  )
}

export const SwapShowAcceptChanges = styled(AutoColumn)`
  background-color: ${({ theme }) => transparentize(0.95, theme.deprecated_primary3)};
  color: ${({ theme }) => theme.accentAction};
  padding: 12px;
  border-radius: 12px;
  margin-top: 8px;
`

export const SwapLayout = styled.div<{ $routeOpen: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ $routeOpen }) => ($routeOpen ? '32px' : '0px')};
  width: 100%;
  padding: 0 10px;
  transition: gap 250ms ease;

  @media only screen and (max-width: 1080px) {
    flex-direction: column;
    gap: 32px;
    align-items: center;
    padding: 0 4px;
  }
`

export const SwapCardColumn = styled.div`
  flex: 0 0 auto;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;

  @media only screen and (max-width: 1080px) {
    width: 100%;
    max-width: 480px;
    padding: 0 0 0 0;
  }
`

export const RoutePanel = styled(SwapWrapper)<{ $open: boolean }>`
  flex: ${({ $open }) => ($open ? '1 1 0' : '0 0 0')};
  max-width: ${({ $open }) => ($open ? '720px' : '0px')};
  min-width: 0;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? 'translateX(0)' : 'translateX(16px)')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 250ms ease, transform 250ms ease, max-width 250ms ease, flex 250ms ease, padding 250ms ease,
    border-width 250ms ease;
  overflow-x: ${({ $open }) => ($open ? 'overlay' : 'hidden')};
  overflow-y: visible;
  padding: ${({ $open }) => ($open ? '16px' : '0')};
  padding-top: ${({ $open }) => ($open ? '12px' : '0')};
  border-width: ${({ $open }) => ($open ? '1px' : '0')};
  -webkit-overflow-scrolling: touch;

  @media only screen and (max-width: 1080px) {
    flex: ${({ $open }) => ($open ? '1 1 100%' : '0 0 0')};
    max-width: 100%;
    width: 100%;
    margin: 0 auto;
    padding-left: 16px;
    padding-right: 16px;
  }

  ${({ $open }) =>
    !$open &&
    css`
      border-color: transparent;
    `}
`
